/**
 * ServiceConfig - Configuration management for all services
 * Handles environment variables and provides service-specific configurations
 */

export class ServiceConfig {
  constructor() {
    this.config = null;
    this._initialize();
  }

  /**
   * Initialize configuration from environment variables
   */
  _initialize() {
    // Check if we should use unified API (legacy mode)
    const useUnifiedAPI = process.env.REACT_APP_USE_UNIFIED_API === 'true';

    if (useUnifiedAPI) {
      this.config = this._getLegacyConfig();
      console.log('🔧 Using UNIFIED API configuration');
    } else {
      this.config = this._getSeparatedConfig();
      console.log('🔧 Using SEPARATED SERVICES configuration');
    }

    this._validateConfig();
  }

  /**
   * Get legacy unified API configuration
   */
  _getLegacyConfig() {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3004';

    return {
      mode: 'unified',
      influencer: {
        serviceName: 'influencer',
        baseURL: baseURL,
        endpoints: {
          base: '/api/influencers',
          byId: (id) => `/api/influencers/${id}`,
          generateHash: (id) => `/api/influencers/${id}/generate-hash`
        }
      },
      images: {
        serviceName: 'images',
        baseURL: baseURL,
        endpoints: {
          base: '/api/images',
          byId: (id) => `/api/images/${id}`,
          byFolder: (folder) => `/api/images?folder=${folder}`,
          transition: '/api/images/transition',
          upload: '/api/images/upload',
          uploadRequest: '/api/images/upload/request',
          uploadConfirm: '/api/images/upload/confirm',
          register: '/api/images/register'
        }
      },
      processing: {
        serviceName: 'processing',
        baseURL: baseURL,
        runpod: {
          apiKey: process.env.REACT_APP_RUNPOD_API_KEY,
          endpoints: {
            cropper: process.env.REACT_APP_RUNPOD_CROPPER_ENDPOINT,
            captioner: process.env.REACT_APP_RUNPOD_CAPTIONER_ENDPOINT,
            training: process.env.REACT_APP_RUNPOD_TRAINING_ENDPOINT
          }
        },
        endpoints: {
          crop: '/api/processing/crop',
          cropBatch: '/api/processing/crop-batch',
          caption: '/api/processing/caption',
          captionBatch: '/api/processing/caption-batch',
          status: (jobId) => `/api/processing/status/${jobId}`
        }
      },
      training: {
        serviceName: 'training',
        baseURL: process.env.REACT_APP_TRAIN_UTILS_API_URL || baseURL,
        runpod: {
          apiKey: process.env.REACT_APP_RUNPOD_API_KEY,
          trainingEndpoint: process.env.REACT_APP_RUNPOD_TRAINING_ENDPOINT
        },
        trainUtils: {
          apiKey: process.env.REACT_APP_TRAIN_UTILS_API_KEY,
          baseURL: process.env.REACT_APP_TRAIN_UTILS_API_URL
        },
        endpoints: {
          zip: '/api/v1/zip',
          settings: '/api/training/settings',
          start: '/api/training/start',
          status: (jobId) => `/api/training/status/${jobId}`
        }
      }
    };
  }

  /**
   * Get separated services configuration
   */
  _getSeparatedConfig() {
    return {
      mode: 'separated',
      influencer: {
        serviceName: 'influencer',
        baseURL: process.env.REACT_APP_INFLUENCER_SERVICE_URL ||
                 process.env.REACT_APP_API_URL ||
                 'http://localhost:3004',
        endpoints: {
          base: process.env.REACT_APP_INFLUENCER_SERVICE_URL ? '/influencers' : '/api/influencers',
          byId: (id) => process.env.REACT_APP_INFLUENCER_SERVICE_URL ? `/influencers/${id}` : `/api/influencers/${id}`,
          generateHash: (id) => process.env.REACT_APP_INFLUENCER_SERVICE_URL ? `/influencers/${id}/generate-hash` : `/api/influencers/${id}/generate-hash`
        }
      },
      images: {
        serviceName: 'images',
        baseURL: process.env.REACT_APP_IMAGE_SERVICE_URL ||
                 process.env.REACT_APP_API_URL ||
                 'http://localhost:3004',
        endpoints: {
          base: process.env.REACT_APP_IMAGE_SERVICE_URL ? '/images' : '/api/images',
          byId: (id) => process.env.REACT_APP_IMAGE_SERVICE_URL ? `/images/${id}` : `/api/images/${id}`,
          byFolder: (folder) => process.env.REACT_APP_IMAGE_SERVICE_URL ? `/images?folder=${folder}` : `/api/images?folder=${folder}`,
          transition: process.env.REACT_APP_IMAGE_SERVICE_URL ? '/images/transition' : '/api/images/transition',
          upload: process.env.REACT_APP_IMAGE_SERVICE_URL ? '/images/upload' : '/api/images/upload',
          uploadRequest: process.env.REACT_APP_IMAGE_SERVICE_URL ? '/images/upload/request' : '/api/images/upload/request',
          uploadConfirm: process.env.REACT_APP_IMAGE_SERVICE_URL ? '/images/upload/confirm' : '/api/images/upload/confirm',
          register: process.env.REACT_APP_IMAGE_SERVICE_URL ? '/images/register' : '/api/images/register'
        },
        // Special endpoints for direct Lambda URLs
        directEndpoints: {
          register: process.env.REACT_APP_IMAGE_REGISTRATION_URL ||
                   'https://aulgvbsj7dsbfd43s3gmqcm52m0dcorn.lambda-url.us-east-1.on.aws/api/images/register'
        }
      },
      processing: {
        serviceName: 'processing',
        baseURL: process.env.REACT_APP_PROCESSING_SERVICE_URL ||
                 process.env.REACT_APP_API_URL ||
                 'http://localhost:3004',
        runpod: {
          apiKey: process.env.REACT_APP_RUNPOD_API_KEY,
          baseURL: 'https://api.runpod.ai/v2',
          endpoints: {
            cropper: process.env.REACT_APP_RUNPOD_CROPPER_ENDPOINT,
            captioner: process.env.REACT_APP_RUNPOD_CAPTIONER_ENDPOINT,
            training: process.env.REACT_APP_RUNPOD_TRAINING_ENDPOINT
          }
        },
        endpoints: {
          crop: '/api/processing/crop',
          cropBatch: '/api/processing/crop-batch',
          caption: '/api/processing/caption',
          captionBatch: '/api/processing/caption-batch',
          status: (jobId) => `/api/processing/status/${jobId}`
        }
      },
      training: {
        serviceName: 'training',
        baseURL: process.env.REACT_APP_TRAINING_SERVICE_URL ||
                 process.env.REACT_APP_API_URL ||
                 'http://localhost:3004',
        runpod: {
          apiKey: process.env.REACT_APP_RUNPOD_API_KEY,
          baseURL: 'https://api.runpod.ai/v2',
          trainingEndpoint: process.env.REACT_APP_RUNPOD_TRAINING_ENDPOINT
        },
        trainUtils: {
          apiKey: process.env.REACT_APP_TRAIN_UTILS_API_KEY,
          baseURL: process.env.REACT_APP_TRAIN_UTILS_API_URL ||
                   'https://u7fb4n10ff.execute-api.us-east-1.amazonaws.com/prod/utils/'
        },
        s3: {
          bucket: process.env.REACT_APP_S3_BUCKET_IMAGES
        },
        endpoints: {
          zip: 'api/v1/zip',  // Remove leading slash to avoid double slash
          settings: '/api/training/settings',
          start: '/api/training/start',
          status: (jobId) => `/api/training/status/${jobId}`
        }
      }
    };
  }

  /**
   * Validate configuration
   */
  _validateConfig() {
    const required = [
      'influencer.baseURL',
      'images.baseURL',
      'processing.baseURL',
      'training.baseURL'
    ];

    const missing = required.filter(path => !this._getNestedValue(this.config, path));

    if (missing.length > 0) {
      console.error('❌ Missing required configuration:', missing);
      throw new Error(`Missing required configuration: ${missing.join(', ')}`);
    }

    // Validate RunPod configuration
    if (!this.config.processing.runpod.apiKey) {
      console.warn('⚠️ Missing REACT_APP_RUNPOD_API_KEY - RunPod operations will fail');
    }

    if (!this.config.training.trainUtils.apiKey) {
      console.warn('⚠️ Missing REACT_APP_TRAIN_UTILS_API_KEY - Training operations may fail');
    }

    console.log('✅ Service configuration validated');
  }

  /**
   * Get nested value from object using dot notation
   */
  _getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Get configuration for all services
   */
  getConfig() {
    return this.config;
  }

  /**
   * Get configuration for a specific service
   */
  getServiceConfig(serviceName) {
    const serviceConfig = this.config[serviceName];
    if (!serviceConfig) {
      throw new Error(`Configuration not found for service: ${serviceName}`);
    }
    return serviceConfig;
  }

  /**
   * Check if we're in unified API mode
   */
  isUnifiedMode() {
    return this.config.mode === 'unified';
  }

  /**
   * Check if we're in separated services mode
   */
  isSeparatedMode() {
    return this.config.mode === 'separated';
  }

  /**
   * Get S3 configuration
   */
  getS3Config() {
    return {
      bucketName: process.env.REACT_APP_S3_BUCKET_IMAGES || 'ohlyfans-images',
      region: process.env.REACT_APP_S3_REGION || 'us-east-1',
      endpoint: process.env.REACT_APP_S3_ENDPOINT,
      accessKeyId: process.env.REACT_APP_S3_ACCESS_KEY,
      secretAccessKey: process.env.REACT_APP_S3_SECRET_KEY,
      s3ForcePathStyle: process.env.REACT_APP_S3_ENDPOINT?.includes('localhost') || false,
      signatureVersion: 'v4'
    };
  }

  /**
   * Get environment information
   */
  getEnvironmentInfo() {
    return {
      isDevelopment: process.env.NODE_ENV === 'development',
      isProduction: process.env.NODE_ENV === 'production',
      mode: this.config.mode,
      hostname: window.location.hostname
    };
  }
}

// Export singleton instance
export const serviceConfig = new ServiceConfig();
export default serviceConfig;